import React from 'react';
import Root from '@/routes/root.tsx';
import { createBrowserRouter, RouterProvider, Navigate, redirect } from 'react-router-dom';
import NotFound from '@/pages/NotFound/NotFound.tsx';
import Clients from '@/pages/Onboarding/Clients';
import Settings from '@/pages/Settings/Settings.tsx';
import LoginForm from '@/pages/Login/loginForm.tsx';
import ResetPasswordForm from '@/pages/ResetPassword/ResetPasswordForm';
import RecoverPasswordForm from '@/pages/RecoverPassword/RecoverPasswordForm';
import AcceptInvitationForm from '@/pages/AcceptInvitation/AcceptInvitationForm';
import useAuthStore from '@/lib/auth';
import { DEFAULT_LANGUAGE, SUPPORTED_LANGUAGES } from '@/constants/languages';
import NaturalPersonRegistryDetails from '@/pages/Onboarding/NaturalPerson/RegistryDetails';
import LegalPersonRegistryDetails from '@/pages/Onboarding/LegalPerson/RegistryDetails';
import { getLanguageFromPath } from '@/utils/utils';
import App from './App';
import Verify from '@/pages/Verify/Verify';
import SupportPage from '@/pages/Support/SupportSignIn';
import MicrocreditRequests from '@/pages/MicrocreditRequests/MicrocreditRequests';
import MicrocreditRequestDetails from '@/pages/MicrocreditRequests/MicrocreditRequestDetails';
import CustomerSupportPage from './pages/CustomerSupport/CustomerSupport';
import BureauReports from '@/pages/Onboarding/BureauAuthorizations/BureauAuthorizations';
import VanCreditRequests from './pages/VanCredits/VanCreditRequests';
import VanCreditRequestDetails from './pages/VanCredits/VanCreditRequestDetails';
import ManageAgentsPage from './pages/ManageAgents/ManageAgents';
import ManageAgentsDetails from './pages/ManageAgents/ManageAgentsDetails/ManageAgentsDetails';
import { ManageAnalysisConfigurations } from './pages/ManageAnalysisConfigurations/ManageAnalysisConfigurations';
import SyncClients from './pages/SyncClients/SyncClients';
import SupportClientsPage from './pages/SupportClients/SupportClients';
import CustomerDetails from './pages/SupportClients/CustomerDetails';
import ManageOrganization from './pages/SupportManageOrganization/ManageOrganization';

const Router: React.FC = () => {
	const { isAuthenticated } = useAuthStore();
	const language = getLanguageFromPath(window.location.pathname);

	// Auth guard loader
	const protectedLoader = async () => {
		// if (!isAuthenticated()) {
		// 	return redirect(`/${language}/signin`);
		// }
		return null;
	};

	const publicLoader = async ({ request }: { request: Request }) => {
		const url = new URL(request.url);
		const token = url.searchParams.get('token');

		if ((url.pathname.includes('/reset') || url.pathname.includes('/confirm_invite')) && token) {
			return null;
		}

		if (isAuthenticated()) {
			return redirect(`/${language}/center/clients`);
		}

		return null;
	};

	const languageMiddleware = async ({
		request,
		params
	}: {
		request: Request;
		params: { lang?: string };
	}) => {
		const { lang } = params;

		if (!lang || !(SUPPORTED_LANGUAGES as readonly string[]).includes(lang)) {
			const url = new URL(request.url);
			const pathname = url.pathname;
			const segments = pathname.split('/');

			if (segments.length === 2 && !segments[1]) {
				return redirect(`/${DEFAULT_LANGUAGE}`);
			}

			if (!(SUPPORTED_LANGUAGES as readonly string[]).includes(segments[1])) {
				segments[1] = DEFAULT_LANGUAGE;
				return redirect(segments.join('/'));
			}
		}

		return null;
	};

	const router = createBrowserRouter([
		{
			path: '/',
			element: <Navigate to={`/${DEFAULT_LANGUAGE}`} replace />,
			errorElement: <Navigate to={`/${language}`} replace />
		},
		{
			path: '/:lang',
			loader: languageMiddleware,
			element: <App />,
			errorElement: <NotFound />,
			children: [
				{
					index: true,
					element: <Navigate to={`signin`} replace />
				},
				{
					path: 'signin',
					element: <LoginForm />,
					errorElement: <NotFound />,
					loader: publicLoader
				},
				{
					path: 'recover',
					element: <RecoverPasswordForm />,
					errorElement: <NotFound />,
					loader: publicLoader
				},
				{
					path: 'verify',
					element: <Verify />,
					errorElement: <NotFound />,
					loader: publicLoader
				},
				{
					path: 'reset',
					element: <ResetPasswordForm />,
					errorElement: <NotFound />,
					loader: publicLoader
				},
				{
					path: 'confirm_invite',
					element: <AcceptInvitationForm />,
					errorElement: <NotFound />,
					loader: publicLoader
				},
				{
					path: 'center',
					element: <Root />,
					errorElement: <NotFound />,
					loader: protectedLoader,
					children: [
						{
							index: true,
							element: <Navigate to={`clients`} replace />
						},
						{
							path: 'clients',
							element: <Clients />,
							loader: async () => {
								// You can load data here for the dashboard
								return { someData: 'clients data' };
							},
							children: [
								{
									path: 'natural/:clientId',
									element: <NaturalPersonRegistryDetails />
								},
								{
									path: 'legal/:clientId',
									element: <LegalPersonRegistryDetails />
								}
							]
						},
						{
							path: 'reports',
							element: <BureauReports />
						},
						{
							path: 'settings',
							element: <Settings />,
							loader: async () => {
								// Load settings data
								return { settingsData: 'settings data' };
							}
						},
						{
							path: 'support',
							element: <SupportPage />
						},
						{
							path: 'customer-support',
							element: <CustomerSupportPage />
						},
						{
							path: 'manage-agents',
							element: <ManageAgentsPage />,
							children: [
								{
									path: 'agents/:agentId',
									element: <ManageAgentsDetails />
								}
							]
						},
						{
							path: 'analysis-configurations',
							element: <ManageAnalysisConfigurations />
						},
						{
							path: 'manage-organization',
							element: <ManageOrganization />
						},
						{
							path: 'support-clients',
							element: <SupportClientsPage />,
							children: [
								{
									path: ':id',
									element: <CustomerDetails />
								}
							]
						},
						{
							path: 'sync-clients',
							element: <SyncClients />
						},
						{
							path: 'microcredit',
							element: <MicrocreditRequests />,
							children: [
								{
									path: 'requests/:requestId',
									element: <MicrocreditRequestDetails />
								}
							]
						},
						{
							path: 'van-credits',
							element: <VanCreditRequests />,
							children: [
								{
									path: 'requests/:requestId',
									element: <VanCreditRequestDetails />
								}
							]
						}
					]
				}
			]
		}
	]);
	return <RouterProvider router={router} />;
};
export default Router;
